import type { PaginationMeta } from '../pagination/index.js';
import type { SlackNotificationConfig } from '../slack-integration/index.js';
import type { EmailOnCompletionPref } from '../notifications/email-on-completion.js';

// ============================================================================
// Schedule Configuration
// ============================================================================

/** Supported schedule frequencies */
export type ScheduleFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'once';

/** Supported timezones — any valid IANA timezone string */
export type ScheduleTimezone = string;

/** Schedule lifecycle states */
export type ScheduleStatus = 'active' | 'paused' | 'completed' | 'deleted';

/** Outcome of a single schedule execution attempt */
export type ScheduleExecutionStatus =
  | 'completed'
  | 'skipped_insufficient_tokens'
  | 'skipped_template_deleted'
  | 'failed';

// ============================================================================
// Constants
// ============================================================================

/** All valid schedule frequencies */
export const SCHEDULE_FREQUENCIES: readonly ScheduleFrequency[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'once',
] as const;

/** Human-readable labels for schedule frequencies */
export const FREQUENCY_LABELS: Record<ScheduleFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  once: 'One-time',
} as const;

/** Day-of-week values matching JavaScript's Date.getDay() */
export type ScheduleWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Human-readable labels for weekdays */
export const WEEKDAY_LABELS: Record<ScheduleWeekday, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
} as const;

/** Short labels for weekday toggle buttons */
export const WEEKDAY_SHORT_LABELS: Record<ScheduleWeekday, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
} as const;

/** All valid weekdays for iteration */
export const SCHEDULE_WEEKDAYS: readonly ScheduleWeekday[] = [0, 1, 2, 3, 4, 5, 6] as const;

// Timezone options, labels, and normalization live in `./timezone.ts` — one
// structured definition list drives the picker, the display label, and the
// Etc/GMT offset → region coercion. Re-exported through `./index.ts`.

/** Valid quarter-hour minutes */
export const QUARTER_HOUR_MINUTES = [0, 15, 30, 45] as const;

// ============================================================================
// Entity Types
// ============================================================================

/** A recurring job schedule */
export interface JobSchedule {
  id: string;
  projectId: string;
  templateId: string;
  organizationId: string;
  createdByUserId: string | null;
  name: string;
  frequency: ScheduleFrequency;
  timezone: ScheduleTimezone;
  /** Hour of day (0-23) when the schedule fires */
  scheduledHour: number;
  /** Minute of hour (0, 15, 30, 45) when the schedule fires */
  scheduledMinute: number;
  /** For weekly/biweekly: which days of the week to run (0=Sun … 6=Sat) */
  weekdays: ScheduleWeekday[];
  /** For monthly: day of the month to run (1–28, or 0 for last day) */
  dayOfMonth: number | null;
  /** For once: the specific date to run (YYYY-MM-DD) */
  scheduledDate: string | null;
  /** For biweekly: anchor date to determine alternating weeks */
  biweeklyAnchor: string | null;
  status: ScheduleStatus;
  nextExecutionAt: string;
  lastExecutedAt: string | null;
  urlOverride: string | null;
  autoCreateGithubIssuesOverride: boolean | null;
  /**
   * Override the template's `autoCreateGithubFeedback` default for jobs
   * created by this schedule. `null` = inherit. Resolution chain at job
   * creation: schedule.override ?? template.value ?? request ?? false.
   */
  autoCreateGithubFeedbackOverride: boolean | null;
  autoCreateJiraFeedbackOverride: boolean | null;
  /**
   * Override the template's `autoCreateJiraIssues` default for jobs created by
   * this schedule. `null` = inherit. Resolution chain at job creation:
   * schedule.override ?? template.value ?? request ?? false. See #5467.
   */
  autoCreateJiraIssuesOverride: boolean | null;
  /**
   * Override the template's `autoCreateLinearIssues` default for jobs created
   * by this schedule. `null` = inherit. Resolution chain at job creation:
   * schedule.override ?? template.value ?? request ?? false.
   */
  autoCreateLinearIssuesOverride: boolean | null;
  /**
   * Override the template's `autoCreateOnlyTesterSurfaced` default for jobs
   * created by this schedule. `null` = inherit the template default.
   */
  autoCreateOnlyTesterSurfacedOverride: boolean | null;
  /**
   * Override the template's `reopenIssuesOnDuplicate` default for jobs
   * created by this schedule. `null` = inherit the template default.
   * See #3430.
   */
  reopenIssuesOnDuplicateOverride: boolean | null;
  /**
   * Override the template's `commentOnDuplicate` default for jobs created by
   * this schedule. `null` = inherit the template default. See #5533.
   */
  commentOnDuplicateOverride: boolean | null;
  /**
   * Override the template's `autoCreateEnhancedVideo` default for jobs
   * created by this schedule. `null` = inherit the template default.
   * See #3516.
   */
  autoCreateEnhancedVideoOverride: boolean | null;
  /**
   * Override the template's `enhanceInstructions` default for jobs created by
   * this schedule. `null` = inherit the template default. Resolution at
   * job creation: schedule.override ?? template.value ?? request ?? false.
   * See #4353 + #4463.
   */
  enhanceInstructionsOverride: boolean | null;
  /** Override Slack notification settings from template */
  slackNotificationOverride: SlackNotificationConfig | null;
  /**
   * Per-schedule email-on-completion preference. Stamped onto every job
   * this schedule fires (replaces the job's default `'inherit'`). Tri-state
   * — `'inherit'` defers up to the project tier at dispatch time. Optional —
   * readers must treat `undefined` as `'inherit'`. See #3514.
   */
  emailOnCompletion?: EmailOnCompletionPref;
  /** Per-schedule in-app-on-completion preference (tri-state). #4863. */
  inAppOnCompletion?: EmailOnCompletionPref;
  /** Per-schedule email-on-failure preference (tri-state). #4863. */
  emailOnFailure?: EmailOnCompletionPref;
  /** Per-schedule in-app-on-failure preference (tri-state). #4863. */
  inAppOnFailure?: EmailOnCompletionPref;
  createdAt: string;
  updatedAt: string;
}

/** A record of a single schedule execution */
export interface JobScheduleExecution {
  id: string;
  scheduleId: string;
  jobId: string | null;
  status: ScheduleExecutionStatus;
  errorMessage: string | null;
  scheduledAt: string;
  executedAt: string;
  createdAt: string;
}

// ============================================================================
// Extended Types (for UI display with joined data)
// ============================================================================

/** Execution record enriched with job data (for UI display) */
export interface JobScheduleExecutionDetail extends JobScheduleExecution {
  jobStatus?: string;
  jobTesterAlias?: string;
  jobTesterAvatarUrl?: string;
  jobTesterProfileId?: string;
  jobTestDurationSeconds?: number;
  jobCompletedAt?: string;
}

/** Schedule with its template name (for project-level lists) */
export interface JobScheduleWithTemplateName extends JobSchedule {
  templateName: string;
}

/** Schedule with project and template names (for org-level overview) */
export interface OrgJobScheduleOverview extends JobScheduleWithTemplateName {
  projectName: string;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateJobScheduleRequest {
  name: string;
  templateId: string;
  frequency?: ScheduleFrequency;
  timezone: ScheduleTimezone;
  /** Hour of day (0-23) */
  scheduledHour: number;
  /** Minute of hour (0, 15, 30, 45) */
  scheduledMinute: number;
  /** For weekly/biweekly: which days of the week to run (0=Sun … 6=Sat) */
  weekdays?: ScheduleWeekday[];
  /** For monthly: day of the month to run (1–28, or 0 for last day) */
  dayOfMonth?: number;
  /** For once: the specific date to run (YYYY-MM-DD) */
  scheduledDate?: string;
  urlOverride?: string;
  autoCreateGithubIssuesOverride?: boolean | null;
  autoCreateGithubFeedbackOverride?: boolean | null;
  autoCreateJiraFeedbackOverride?: boolean | null;
  autoCreateJiraIssuesOverride?: boolean | null;
  autoCreateLinearIssuesOverride?: boolean | null;
  autoCreateOnlyTesterSurfacedOverride?: boolean | null;
  reopenIssuesOnDuplicateOverride?: boolean | null;
  commentOnDuplicateOverride?: boolean | null;
  autoCreateEnhancedVideoOverride?: boolean | null;
  enhanceInstructionsOverride?: boolean | null;
  slackNotificationOverride?: SlackNotificationConfig;
  /** Tri-state email-on-completion stamped onto every job this schedule fires. */
  emailOnCompletion?: EmailOnCompletionPref;
  /** Tri-state in-app-on-completion stamped onto every job this schedule fires. #4863. */
  inAppOnCompletion?: EmailOnCompletionPref;
  /** Tri-state email-on-failure stamped onto every job this schedule fires. #4863. */
  emailOnFailure?: EmailOnCompletionPref;
  /** Tri-state in-app-on-failure stamped onto every job this schedule fires. #4863. */
  inAppOnFailure?: EmailOnCompletionPref;
}

export interface UpdateJobScheduleRequest {
  name?: string;
  templateId?: string;
  frequency?: ScheduleFrequency;
  timezone?: ScheduleTimezone;
  /** Hour of day (0-23) */
  scheduledHour?: number;
  /** Minute of hour (0, 15, 30, 45) */
  scheduledMinute?: number;
  /** For weekly/biweekly: which days of the week to run (0=Sun … 6=Sat) */
  weekdays?: ScheduleWeekday[];
  /** For monthly: day of the month to run (1–28, or 0 for last day) */
  dayOfMonth?: number | null;
  /** For once: the specific date to run (YYYY-MM-DD) */
  scheduledDate?: string | null;
  status?: 'active' | 'paused';
  urlOverride?: string | null;
  autoCreateGithubIssuesOverride?: boolean | null;
  autoCreateGithubFeedbackOverride?: boolean | null;
  autoCreateJiraFeedbackOverride?: boolean | null;
  autoCreateJiraIssuesOverride?: boolean | null;
  autoCreateLinearIssuesOverride?: boolean | null;
  autoCreateOnlyTesterSurfacedOverride?: boolean | null;
  reopenIssuesOnDuplicateOverride?: boolean | null;
  commentOnDuplicateOverride?: boolean | null;
  autoCreateEnhancedVideoOverride?: boolean | null;
  enhanceInstructionsOverride?: boolean | null;
  slackNotificationOverride?: SlackNotificationConfig | null;
  /** Tri-state email-on-completion stamped onto every job this schedule fires. */
  emailOnCompletion?: EmailOnCompletionPref;
  /** Tri-state in-app-on-completion stamped onto every job this schedule fires. #4863. */
  inAppOnCompletion?: EmailOnCompletionPref;
  /** Tri-state email-on-failure stamped onto every job this schedule fires. #4863. */
  emailOnFailure?: EmailOnCompletionPref;
  /** Tri-state in-app-on-failure stamped onto every job this schedule fires. #4863. */
  inAppOnFailure?: EmailOnCompletionPref;
}

export interface JobScheduleListResponse {
  items: JobSchedule[];
  pagination: PaginationMeta;
}

export interface JobScheduleExecutionListResponse {
  items: JobScheduleExecutionDetail[];
  pagination: PaginationMeta;
}
